import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Globe, Mail, Lock, User, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const { lang, setLang } = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | register

  const sk = lang === "sk";

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      if (mode === "register") {
        const r = await register(email, password, name);
        if (r?.needsConfirm) {
          setInfo(sk
            ? "Účet vytvorený! Skontroluj e-mail (aj SPAM) a potvrď registráciu, potom sa prihlás."
            : "Account created! Check your e-mail (including SPAM), confirm, then sign in.");
          setMode("login");
        } else {
          nav("/");
        }
      } else {
        await login(email, password);
        nav("/");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || (sk ? "Chyba prihlásenia" : "Sign-in error"));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-white/[0.03] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-sm " +
    "outline-none transition-colors placeholder:text-white/25 " +
    "focus:border-fuchsia-500/60 focus:bg-white/[0.05]";

  return (
    <div className="min-h-screen flex bg-[#08070d] text-white relative overflow-hidden">
      <style>{`
        @keyframes auraPulse {
          0%,100% { opacity:.5; transform:scale(1); }
          50%     { opacity:.95; transform:scale(1.09); }
        }
        @keyframes markFloat {
          0%,100% { transform:translateY(0); filter:drop-shadow(0 0 26px rgba(155,92,255,.5)); }
          50%     { transform:translateY(-12px); filter:drop-shadow(0 0 52px rgba(155,92,255,.9)); }
        }
        @keyframes ringSpin { to { transform:rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .fade-1 { animation:fadeUp .7s .05s cubic-bezier(.2,.7,.3,1) both; }
        .fade-2 { animation:fadeUp .7s .18s cubic-bezier(.2,.7,.3,1) both; }
        .fade-3 { animation:fadeUp .7s .32s cubic-bezier(.2,.7,.3,1) both; }
        @media (prefers-reduced-motion:reduce){ *{ animation:none !important; } }
      `}</style>

      {/* mriežka + ambientné aury cez celú obrazovku */}
      <div className="absolute inset-0 pointer-events-none opacity-[.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse 90% 80% at 40% 45%, #000 25%, transparent 75%)",
        }} />
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(52% 46% at 30% 40%, rgba(155,92,255,.20), transparent 70%)",
          animation: "auraPulse 5.5s ease-in-out infinite",
        }} />
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(40% 38% at 82% 78%, rgba(217,70,239,.13), transparent 70%)",
          animation: "auraPulse 5.5s ease-in-out 2.7s infinite",
        }} />

      {/* ĽAVÁ — logo + pitch */}
      <div className="hidden lg:flex w-[55%] relative flex-col justify-between p-12 z-10">
        <div className="fade-1 font-serif italic text-4xl tracking-tight select-none">
          dash<span className="text-fuchsia-400">.</span>
          <span className="ml-3 align-middle font-sans not-italic font-mono text-[9px] uppercase tracking-[.3em] text-white/30">
            Lummin Collective
          </span>
        </div>

        {/* logo s orbitami */}
        <div className="fade-2 relative flex justify-center items-center py-4">
          <div className="absolute w-[430px] h-[430px] rounded-full border border-fuchsia-500/15"
            style={{ animation: "ringSpin 40s linear infinite" }}>
            <span className="absolute -top-[5px] left-1/2 w-2.5 h-2.5 rounded-full bg-fuchsia-400/80 shadow-[0_0_12px_rgba(217,70,239,.8)]" />
          </div>
          <div className="absolute w-[330px] h-[330px] rounded-full border border-[#b16bff]/20"
            style={{ animation: "ringSpin 26s linear infinite reverse" }}>
            <span className="absolute top-1/2 -right-[4px] w-2 h-2 rounded-full bg-[#c8ff00]/80 shadow-[0_0_10px_rgba(200,255,0,.7)]" />
          </div>
          <img src="/lummin-mark.png" alt="Lummin Collective" draggable={false}
            className="w-[300px] relative"
            style={{ animation: "markFloat 6s ease-in-out infinite" }} />
        </div>

        <div className="fade-3 space-y-4 max-w-lg">
          <div className="font-mono text-[10px] uppercase tracking-[.26em] text-white/35">
            <span className="text-[#c8ff00]">{sk ? "Vytvor" : "Create"}</span>
            {" · "}
            <span className="text-[#b16bff]">{sk ? "Napoj" : "Connect"}</span>
            {" · "}
            <span className="text-fuchsia-400">{sk ? "Zarábaj" : "Earn"}</span>
          </div>
          <h1 className="font-serif text-[44px] leading-[1.04]">
            {sk ? "Jedna postava." : "One character."}<br />
            <span className="italic bg-gradient-to-r from-[#c8ff00] via-[#b16bff] to-[#d946ef] bg-clip-text text-transparent">
              {sk ? "Nekonečný príjem." : "Endless income."}
            </span>
          </h1>
          <p className="text-sm text-white/50 leading-relaxed max-w-md">
            {sk
              ? "AI chatbot odpisuje fanúšikom tvojím hlasom, sám ponúka platené fotky v správnej chvíli a účtuje za teba — na Fanvue aj Telegrame."
              : "An AI chatbot replies to fans in your voice, offers paid photos at the right moment and charges for you — on Fanvue and Telegram."}
          </p>
          <div className="flex gap-6 pt-1 font-mono text-[10px] uppercase tracking-widest text-white/35">
            <span><b className="text-white/85">8</b> {sk ? "AI modelov" : "AI models"}</span>
            <span><b className="text-white/85">24/7</b> chatbot</span>
            <span><b className="text-white/85">2</b> {sk ? "platformy" : "platforms"}</span>
            <span><b className="text-white/85">0</b> {sk ? "nočných zmien" : "night shifts"}</span>
          </div>
        </div>
      </div>

      {/* PRAVÁ — glass karta */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 z-10">
        <div className="w-full max-w-[420px]">
          {/* mobil: malé logo hore */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/lummin-mark.png" alt="" className="w-28"
              style={{ filter: "drop-shadow(0 0 20px rgba(155,92,255,.5))" }} />
          </div>

          <div className="fade-2 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 shadow-[0_20px_60px_rgba(0,0,0,.5)]">
            {/* hlavička karty */}
            <div className="flex items-center justify-between mb-7">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[.24em] text-white/35">
                  {mode === "login" ? (sk ? "Vitaj späť" : "Welcome back") : (sk ? "Začíname" : "Let's start")}
                </div>
                <div className="font-serif text-2xl mt-1">
                  {mode === "login"
                    ? (sk ? "Prihlásenie" : "Sign in")
                    : (sk ? "Nový účet" : "Create account")}
                </div>
              </div>
              <button type="button" onClick={() => setLang(sk ? "en" : "sk")}
                data-testid="lang-toggle"
                className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-white/40 hover:text-white transition-colors border border-white/10 rounded-lg px-3 py-2">
                <Globe size={12} /> {sk ? "EN" : "SK"}
              </button>
            </div>

            {/* prepínač login/register */}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/5 mb-6">
              {[
                { v: "login", l: sk ? "Prihlásiť" : "Sign in" },
                { v: "register", l: sk ? "Registrovať" : "Sign up" },
              ].map((o) => (
                <button key={o.v} type="button"
                  onClick={() => { setMode(o.v); setError(null); setInfo(null); }}
                  data-testid={`mode-${o.v}`}
                  className={`py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${
                    mode === o.v
                      ? "bg-gradient-to-r from-fuchsia-600/80 to-[#b16bff]/80 text-white shadow-[0_4px_18px_rgba(217,70,239,.35)]"
                      : "text-white/40 hover:text-white/70"
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div className="relative">
                  <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder={sk ? "Meno / prezývka" : "Name / nickname"}
                    data-testid="input-name" className={field} />
                </div>
              )}
              <div className="relative">
                <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" data-testid="input-email" className={field} />
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={sk ? "Heslo" : "Password"} data-testid="input-password" className={field} />
              </div>

              {error && (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">
                  {error}
                </div>
              )}
              {info && (
                <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
                  {info}
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="btn-submit"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm
                  bg-gradient-to-r from-fuchsia-600 to-[#b16bff] hover:from-fuchsia-500 hover:to-[#c07dff]
                  shadow-[0_10px_34px_rgba(217,70,239,.35)] hover:shadow-[0_14px_42px_rgba(217,70,239,.5)]
                  transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {loading
                  ? (sk ? "Moment…" : "One sec…")
                  : mode === "login" ? (sk ? "Vstúpiť do panelu" : "Enter the panel")
                                     : (sk ? "Vytvoriť účet" : "Create account")}
                {!loading && <ArrowRight size={16} />}
              </button>
            </form>

            <div className="mt-6 text-center text-[11px] text-white/30">
              {mode === "login" ? (
                <>{sk ? "Nemáš účet?" : "No account yet?"}{" "}
                  <button type="button" onClick={() => setMode("register")}
                    className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2">
                    {sk ? "Zaregistruj sa" : "Sign up"}
                  </button></>
              ) : (
                <>{sk ? "Už máš účet?" : "Already have an account?"}{" "}
                  <button type="button" onClick={() => setMode("login")}
                    className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2">
                    {sk ? "Prihlás sa" : "Sign in"}
                  </button></>
              )}
            </div>
          </div>

          <div className="mt-6 text-center font-mono text-[9px] uppercase tracking-[.24em] text-white/20">
            dash.lummio.app · from Lummin Community
          </div>
        </div>
      </div>
    </div>
  );
}
